import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/auth.store';
import { useQueueRealtime } from '../hooks/useQueueRealtime';
import { apiGet, apiPost } from '../lib/api';
import { StaffRole } from '@bisa/shared';
import type { QueueStateDto, QueueEntryDto } from '@bisa/shared';
import { TicketStatus } from '@bisa/shared';
import { Button } from '../components/ui/Button';
import { TicketStatusBadge } from '../components/ui/Badge';
import { PageSpinner } from '../components/ui/Spinner';

interface ServiceCategory {
  id: string;
  name: string;
  prefix: string;
  isActive: boolean;
  avgServiceTimeMins: number;
}

interface ServiceSlot {
  serviceCategoryId: string;
  serviceName: string;
  prefix: string;
  queue: QueueStateDto | null;
}

function UnstartedCard({
  slot,
  facilityId,
  role,
  onOpen,
}: {
  slot: ServiceSlot;
  facilityId: string;
  role: StaffRole;
  onOpen: () => void;
}) {
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const canOpen = role === StaffRole.FRONT_DESK || role === StaffRole.ADMIN;

  const handleOpen = async () => {
    setOpening(true);
    setOpenError(null);
    try {
      await apiPost(`/facilities/${facilityId}/queues/open`, {
        serviceCategoryId: slot.serviceCategoryId,
      });
      onOpen();
    } catch (e) {
      setOpenError(e instanceof Error ? e.message : String(e));
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="rounded-xl border bg-white shadow-sm opacity-60">
      <div className="flex items-start justify-between px-5 py-4 border-b">
        <div>
          <h3 className="font-semibold text-gray-900">{slot.serviceName}</h3>
          <span className="font-mono text-xs text-blue-600 font-bold">{slot.prefix}-</span>
        </div>
        <span className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500">
          Not started today
        </span>
      </div>
      <div className="px-5 py-3 space-y-2">
        {canOpen ? (
          <Button size="sm" loading={opening} onClick={handleOpen}>Open Queue</Button>
        ) : (
          <p className="text-xs text-gray-400">Queue not yet open today.</p>
        )}
        {openError && <p className="text-xs text-red-600">{openError}</p>}
      </div>
    </div>
  );
}

function QueueCard({
  queue,
  facilityId,
  role,
  onAction,
}: {
  queue: QueueStateDto;
  facilityId: string;
  role: StaffRole;
  onAction: () => void;
}) {
  const [acting, setActing] = useState(false);
  const [bannerActing, setBannerActing] = useState(false);
  const [lastCalledEntry, setLastCalledEntry] = useState<QueueEntryDto | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState(false);
  const [activeTickets, setActiveTickets] = useState<QueueEntryDto[]>([]);
  const [servedTickets, setServedTickets] = useState<QueueEntryDto[]>([]);
  const [ticketTab, setTicketTab] = useState<'active' | 'served'>('active');
  const [ticketSearch, setTicketSearch] = useState('');

  const [showWalkin, setShowWalkin] = useState(false);
  const [walkinName, setWalkinName] = useState('');
  const [walkinLoading, setWalkinLoading] = useState(false);
  const [walkinCode, setWalkinCode] = useState<string | null>(null);

  const canCallNext = role === StaffRole.FRONT_DESK || role === StaffRole.ADMIN;
  const canMarkServed = role === StaffRole.PROVIDER || role === StaffRole.FRONT_DESK || role === StaffRole.ADMIN;
  const canSkip = canCallNext;
  const canViewServed = role === StaffRole.FRONT_DESK || role === StaffRole.ADMIN;

  const fetchTicketLists = useCallback(async () => {
    const [active, served] = await Promise.all([
      apiGet<QueueEntryDto[]>(`/queue-entries?queueId=${queue.queueId}&status=WAITING,CALLED,SKIPPED`),
      canViewServed
        ? apiGet<QueueEntryDto[]>(`/queue-entries?queueId=${queue.queueId}&status=SERVED`)
        : Promise.resolve<QueueEntryDto[]>([]),
    ]);
    setActiveTickets(active);
    setServedTickets(served);
  }, [queue.queueId, canViewServed]);

  const refreshAll = useCallback(() => {
    onAction();
    if (expanded) fetchTicketLists();
  }, [onAction, expanded, fetchTicketLists]);

  // Auto-detect the currently-called ticket whenever the queue state changes.
  // This ensures the banner + quick buttons appear even after a page refresh
  // or when a different browser/user called the next patient.
  useEffect(() => {
    if (!queue.isOpen || !queue.currentlyServing) return;
    apiGet<QueueEntryDto[]>(`/queue-entries?queueId=${queue.queueId}&status=CALLED`)
      .then((entries) => {
        if (entries.length > 0) {
          // Only set if banner isn't already showing a more-recent entry
          setLastCalledEntry((prev) => prev ?? entries[0]);
        } else {
          // No CALLED tickets — clear stale banner
          setLastCalledEntry(null);
        }
      })
      .catch(() => {});
  }, [queue.queueId, queue.currentlyServing, queue.isOpen]);

  // Also clear banner when a served/skipped action propagates through the active list
  useEffect(() => {
    if (!lastCalledEntry || !expanded) return;
    const stillCalled = activeTickets.some(
      (t) => t.id === lastCalledEntry.id && t.status === TicketStatus.CALLED,
    );
    if (!stillCalled) setLastCalledEntry(null);
  }, [activeTickets, lastCalledEntry, expanded]);

  const act = async (fn: () => Promise<unknown>) => {
    setActing(true);
    setActionError(null);
    try {
      await fn();
      refreshAll();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setActing(false);
    }
  };

  const callNext = async () => {
    setActing(true);
    setActionError(null);
    try {
      const entry = await apiPost<QueueEntryDto>(`/queues/${queue.queueId}/next`, {});
      setLastCalledEntry(entry);
      refreshAll();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setActing(false);
    }
  };

  const serveBannerPatient = async () => {
    if (!lastCalledEntry) return;
    setBannerActing(true);
    setActionError(null);
    try {
      await apiPost(`/queue-entries/${lastCalledEntry.id}/served`, {});
      setLastCalledEntry(null);
      refreshAll();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBannerActing(false);
    }
  };

  const skipBannerPatient = async () => {
    if (!lastCalledEntry) return;
    setBannerActing(true);
    setActionError(null);
    try {
      await apiPost(`/queue-entries/${lastCalledEntry.id}/skip`, {});
      setLastCalledEntry(null);
      refreshAll();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBannerActing(false);
    }
  };

  const toggleOpen = () =>
    act(() =>
      queue.isOpen
        ? apiPost(`/facilities/${facilityId}/queues/close/${queue.queueId}`, {})
        : apiPost(`/facilities/${facilityId}/queues/open`, { serviceCategoryId: queue.serviceCategoryId }),
    );

  const toggleExpanded = async () => {
    if (expanded) {
      setExpanded(false);
    } else {
      await fetchTicketLists();
      setExpanded(true);
    }
  };

  const issueWalkin = async () => {
    setWalkinLoading(true);
    setWalkinCode(null);
    try {
      const res = await apiPost<{ ticketCode: string }>(`/queues/${queue.queueId}/walkin`, {
        patientName: walkinName.trim() || undefined,
      });
      setWalkinCode(res.ticketCode);
      setWalkinName('');
      refreshAll();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setWalkinLoading(false);
    }
  };

  const displayTickets = (ticketTab === 'active' ? activeTickets : servedTickets).filter((t) => {
    if (!ticketSearch) return true;
    const q = ticketSearch.toLowerCase();
    return (
      t.ticketCode.toLowerCase().includes(q) ||
      (t.patientName?.toLowerCase().includes(q) ?? false)
    );
  });

  return (
    <div className={`rounded-xl border bg-white shadow-sm ${!queue.isOpen ? 'opacity-60' : ''}`}>
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b">
        <div>
          <h3 className="font-semibold text-gray-900">{queue.serviceName}</h3>
          <span className="font-mono text-xs text-blue-600 font-bold">{queue.prefix}-</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${queue.isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {queue.isOpen ? 'Open' : 'Closed'}
          </span>
          {(role === StaffRole.FRONT_DESK || role === StaffRole.ADMIN) && (
            <Button size="sm" variant="ghost" loading={acting} onClick={toggleOpen}>
              {queue.isOpen ? 'Close' : 'Open'}
            </Button>
          )}
        </div>
      </div>

      {/* NOW CALLING banner — quick serve/skip without opening the ticket list */}
      {lastCalledEntry && (
        <div className="border-b bg-green-600 px-5 py-4 text-white">
          <p className="text-xs font-semibold uppercase tracking-widest opacity-80">Now Calling</p>
          <p className="text-5xl font-black font-mono mt-1 leading-none">{lastCalledEntry.ticketCode}</p>
          {lastCalledEntry.patientName && (
            <p className="text-sm mt-1 opacity-90">{lastCalledEntry.patientName}</p>
          )}
          <div className="flex gap-3 mt-4">
            {canMarkServed && (
              <button
                disabled={bannerActing}
                onClick={serveBannerPatient}
                className="flex-1 rounded-lg bg-white text-green-700 font-bold py-2.5 text-sm hover:bg-green-50 disabled:opacity-50 transition-colors"
              >
                {bannerActing ? '…' : '✓ Served'}
              </button>
            )}
            {canSkip && (
              <button
                disabled={bannerActing}
                onClick={skipBannerPatient}
                className="flex-1 rounded-lg bg-green-500 text-white font-bold py-2.5 text-sm hover:bg-green-400 disabled:opacity-50 transition-colors"
              >
                {bannerActing ? '…' : 'Skip'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error banner */}
      {actionError && (
        <div className="border-b bg-red-50 px-5 py-2 flex items-center justify-between gap-3">
          <p className="text-sm text-red-700 flex-1">{actionError}</p>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600 text-lg leading-none flex-shrink-0">×</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 divide-x">
        <Stat
          label="Serving"
          value={queue.currentlyServing != null ? `${queue.prefix}-${String(queue.currentlyServing).padStart(3, '0')}` : '—'}
          highlight
        />
        <Stat label="Waiting" value={String(queue.waitingCount)} />
        <Stat label="Est. wait" value={`${queue.estimatedWaitMins} min`} />
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 px-5 py-3 border-t bg-gray-50">
        {queue.isOpen && canCallNext && (
          <Button loading={acting} onClick={callNext} disabled={queue.waitingCount === 0}>
            Next Patient
          </Button>
        )}
        {queue.isOpen && canCallNext && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { setShowWalkin((v) => !v); setWalkinCode(null); }}
          >
            + Walk-in
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={toggleExpanded}>
          {expanded ? 'Hide tickets' : 'View tickets'}
        </Button>
      </div>

      {/* Walk-in form */}
      {showWalkin && queue.isOpen && (
        <div className="border-t bg-blue-50 px-5 py-3 space-y-2">
          <p className="text-xs font-medium text-blue-800">Issue walk-in ticket</p>
          <div className="flex gap-2 items-center">
            <input
              className="flex-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm"
              placeholder="Patient name (optional)"
              value={walkinName}
              onChange={(e) => setWalkinName(e.target.value)}
            />
            <Button size="sm" loading={walkinLoading} onClick={issueWalkin}>
              Issue
            </Button>
          </div>
          {walkinCode && (
            <p className="text-sm font-bold text-blue-700">
              Ticket issued: <span className="font-mono">{walkinCode}</span>
            </p>
          )}
        </div>
      )}

      {/* Ticket list panel */}
      {expanded && (
        <div className="border-t">
          {/* Tabs */}
          <div className="flex border-b bg-white">
            <button
              className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                ticketTab === 'active'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setTicketTab('active')}
            >
              Active ({activeTickets.length})
            </button>
            {canViewServed && (
              <button
                className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                  ticketTab === 'served'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setTicketTab('served')}
              >
                Served ({servedTickets.length})
              </button>
            )}
          </div>

          {/* Search */}
          <div className="px-4 py-2 border-b bg-white">
            <input
              className="w-full rounded border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-300"
              placeholder="Search by ticket # or patient name…"
              value={ticketSearch}
              onChange={(e) => setTicketSearch(e.target.value)}
            />
          </div>

          {/* Rows */}
          <div className="divide-y max-h-64 overflow-y-auto">
            {displayTickets.length === 0 ? (
              <p className="px-5 py-4 text-sm text-gray-400">
                {ticketSearch ? 'No tickets match your search.' : 'No tickets here.'}
              </p>
            ) : (
              displayTickets.map((t) => (
                <TicketRow
                  key={t.id}
                  ticket={t}
                  prefix={queue.prefix}
                  canServe={canMarkServed && ticketTab === 'active'}
                  canSkip={canSkip && ticketTab === 'active'}
                  facilityId={facilityId}
                  onAction={refreshAll}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="px-5 py-3 text-center">
      <p className={`text-2xl font-bold ${highlight ? 'text-blue-600' : 'text-gray-900'}`}>{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
    </div>
  );
}

function TicketRow({
  ticket,
  prefix,
  canServe,
  canSkip,
  facilityId: _facilityId,
  onAction,
}: {
  ticket: QueueEntryDto;
  prefix: string;
  canServe: boolean;
  canSkip: boolean;
  facilityId: string;
  onAction: () => void;
}) {
  const [acting, setActing] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    setActing(true);
    setRowError(null);
    try {
      await fn();
      onAction();
    } catch (e) {
      setRowError(e instanceof Error ? e.message : String(e));
    } finally {
      setActing(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between px-5 py-2.5">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-mono font-bold text-sm w-16 flex-shrink-0">{ticket.ticketCode}</span>
          <TicketStatusBadge status={ticket.status} />
          {ticket.patientName && (
            <span className="text-sm text-gray-600 truncate">{ticket.patientName}</span>
          )}
        </div>
        <div className="flex gap-1 flex-shrink-0 ml-2">
          {ticket.status === TicketStatus.CALLED && canServe && (
            <Button
              size="sm"
              variant="secondary"
              loading={acting}
              onClick={() => act(() => apiPost(`/queue-entries/${ticket.id}/served`, {}))}
            >
              Served
            </Button>
          )}
          {ticket.status === TicketStatus.CALLED && canSkip && (
            <Button
              size="sm"
              variant="ghost"
              loading={acting}
              onClick={() => act(() => apiPost(`/queue-entries/${ticket.id}/skip`, {}))}
            >
              Skip
            </Button>
          )}
          {ticket.status === TicketStatus.SKIPPED && canSkip && (
            <Button
              size="sm"
              variant="ghost"
              loading={acting}
              onClick={() => act(() => apiPost(`/queue-entries/${ticket.id}/recall`, {}))}
            >
              Recall
            </Button>
          )}
        </div>
      </div>
      {rowError && <p className="px-5 pb-2 text-xs text-red-600">{rowError}</p>}
    </div>
  );
}

export function QueuePage() {
  const { staff } = useAuthStore();
  const facilityId = staff!.facilityId;
  const { queues, loading, error, refetch } = useQueueRealtime(facilityId);
  const [services, setServices] = useState<ServiceCategory[]>([]);

  const loadServices = useCallback(async () => {
    try {
      const data = await apiGet<ServiceCategory[]>(`/admin/facilities/${facilityId}/services`);
      setServices(data.filter((s) => s.isActive));
    } catch {
      // non-critical
    }
  }, [facilityId]);

  useEffect(() => { loadServices(); }, [loadServices]);

  const slots: ServiceSlot[] = services.map((s) => ({
    serviceCategoryId: s.id,
    serviceName: s.name,
    prefix: s.prefix,
    queue: queues.find((q) => q.serviceCategoryId === s.id) ?? null,
  }));

  const orphanQueues = queues.filter(
    (q) => !services.some((s) => s.id === q.serviceCategoryId),
  );

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Live Queue</h1>
          <p className="text-sm text-gray-500 mt-0.5">{staff?.facilityName}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={refetch}>Refresh</Button>
      </div>

      {loading && <PageSpinner />}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && slots.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
          <p className="text-gray-500">No active services configured.</p>
          <p className="text-sm text-gray-400 mt-1">
            Set up departments and service categories under Departments / Services.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {slots.map((slot) =>
          slot.queue ? (
            <QueueCard
              key={slot.queue.queueId}
              queue={slot.queue}
              facilityId={facilityId}
              role={staff!.role}
              onAction={refetch}
            />
          ) : (
            <UnstartedCard
              key={slot.serviceCategoryId}
              slot={slot}
              facilityId={facilityId}
              role={staff!.role}
              onOpen={refetch}
            />
          ),
        )}
        {orphanQueues.map((q) => (
          <QueueCard
            key={q.queueId}
            queue={q}
            facilityId={facilityId}
            role={staff!.role}
            onAction={refetch}
          />
        ))}
      </div>
    </div>
  );
}
